import { CATALOG } from "./catalog";
import { resolveValue } from "./cascade";
import { layersFor, type Rows } from "./store";
import type { CatalogContext, Scope, Setting } from "./types";
import { FIRST_INSTANCE, type Entry } from "../funnel/order";
import {
  FIXED_PAGES,
  MANAGED_PAGES,
  closedKey,
  isFixed,
  navKey,
  publicKey,
  tileKey,
  type SitePage,
} from "../pages/registry";
import { withAdded } from "../pages/repeatable";
import { stepPages } from "../pages/stepPages";

/**
 * WHAT IS ACTUALLY SET, AND WHERE THE TYPOLOGIES PART WAYS.
 *
 * The panel answers one setting at a time. This answers the question you only
 * ask after an hour's work: what have I changed, and what does an exhibitor see
 * that everyone else does not? With a couple of hundred settings and several
 * typologies, nobody holds that in their head — and an override forgotten in a
 * corner is found by a participant, not by its author.
 *
 * Pure on purpose: it takes the rows and gives back a table. No DOM, no context,
 * so the awkward parts — a value overridden to the SAME thing, a typology that
 * inherits, a `false` that means something — are settled by tests rather than by
 * squinting at the screen.
 *
 * ⚠️ ONE LANGUAGE AT A TIME, the one being read. The cascade has two axes and a
 * table crossing both would have as many columns as languages × typologies. The
 * modal says which language it is showing; switching language rebuilds it.
 */

export type TypoCell = {
  typoId: number;
  /** Resolved for this typology — inherited values included. */
  value: unknown;
  /** Written AT this typology, as opposed to inherited from underneath. */
  own: boolean;
};

export type OverviewRow = {
  key: string;
  label: string;
  /** The family it belongs to, so the table reads like the panel. */
  group: string;
  /**
   * Le sous-titre DANS la famille, quand elle en a un.
   *
   * ⚠️ SANS LUI, DES LIGNES ENGENDRÉES SE LISENT PAREIL. La famille du parcours produit
   * une ligne « Servie » par étape, et c'est sa SECTION qui dit laquelle — le nom de
   * l'étape. Le tableau montrait « Servie · Étapes du parcours » plusieurs fois de
   * suite, sans rien pour les départager.
   */
  section?: string;
  /**
   * Catalog scope the setting came from — `site`, or a screen key. The KEY, not a
   * name: naming screens is the registry's job, and duplicating it here would
   * leave two lists to keep in step.
   */
  scope: string;
  /**
   * LE TYPE ET LES CHOIX DU RÉGLAGE, pour que le tableau lise une valeur comme l'étape.
   *
   * ⚠️ UN RÉGLAGE À CHOIX RANGE UNE VALEUR, PAS CE QU'ON A CHOISI. « Formulaire servi »
   * vaut `5693`, la place du message `"left"` : affichés bruts, ils ne disaient rien à
   * l'organisateur qui avait choisi un nom (bug #293). Les formulaires n'ont pas de
   * choix ici (les contextes sont bâtis sans eux, voir plus bas) : le tableau les
   * nomme d'après ceux de l'événement.
   */
  type: Setting["type"];
  options?: Setting["options"];
  /** The value with no typology: what an ordinary participant gets. */
  common: unknown;
  cells: TypoCell[];
  /** At least one typology resolves to something other than the common value. */
  differs: boolean;
  /** A value exists somewhere — otherwise the setting has never been touched. */
  isSet: boolean;
};

/**
 * Every catalogued setting, with the family and screen it came from.
 *
 * The typologies are needed because a group may GENERATE its settings from them
 * (a switch per typology). Without them those rows would be missing from the
 * recap — and the recap's whole job is to say what is set and where the
 * typologies diverge, so leaving out the settings that are ABOUT typologies would
 * be the worst possible omission.
 */
type Catalogued = {
  setting: Setting;
  group: string;
  scope: string;
  /** Ce que le site lit quand la clé manque — seules les lignes des pages en portent un. */
  absent?: boolean;
};

function catalogued(
  contextes: ReadonlyArray<CatalogContext>,
  sitePages: ReadonlyArray<SitePage>,
): Catalogued[] {
  const out: Catalogued[] = [];
  /**
   * ═══ UNE CLÉ, UNE LIGNE ═══
   *
   * ⚠️ UNE FAMILLE PEUT ÊTRE DÉCLARÉE DANS PLUSIEURS PORTÉES, et celle du parcours l'est
   * dans celle de CHAQUE étape — c'est ce qui la fait apparaître sur chacune d'elles dans
   * le panneau. Parcourues à plat, ses lignes revenaient donc onze fois : le
   * récapitulatif affichait « Servie » onze fois par étape, mesuré.
   *
   * ⚠️ ET CE N'ÉTAIT PAS QUE DE L'AFFICHAGE. Le tableau se donne `setting.key` comme clé
   * de rendu : onze lignes portaient la MÊME, et React ne sait plus rapprocher les
   * anciennes des nouvelles quand la liste change. Passer de « tout » à « différences »
   * laissait 98 lignes là où 18 étaient attendues — c'est le défaut signalé. Une clé
   * unique par construction vaut mieux qu'une clé composée à l'affichage : ici la
   * duplication n'avait aucune raison d'exister.
   *
   * On garde la PREMIÈRE rencontrée : elle porte la même valeur, le même libellé et la
   * même famille que ses jumelles, seule sa portée diffère.
   */
  const seen = new Set<string>();
  for (const [scope, groups] of Object.entries(CATALOG)) {
    for (const group of groups ?? []) {
      // Une passe de contexte par instance : la première (l'événement la donne déjà) puis
      // chacune des suivantes, pour faire apparaître leurs clés suffixées. Le dédoublonnage
      // garde la première rencontrée — voir `everySetting`, même patron.
      for (const context of contextes) {
        const settings =
          typeof group.settings === "function"
            ? group.settings(context)
            : group.settings;
        for (const setting of settings) {
          if (seen.has(setting.key)) continue;
          seen.add(setting.key);
          out.push({ setting, group: group.title, scope });
        }
      }
      // Les interrupteurs des pages suivent le gestionnaire qui les pilote : dans sa
      // famille, à sa place — voir `pageSwitches`.
      const manages = contextes.some((context) =>
        (typeof group.settings === "function"
          ? group.settings(context)
          : group.settings
        ).some((setting) => setting.type === "pages"),
      );
      if (manages) {
        for (const entry of pageSwitches(sitePages)) {
          if (seen.has(entry.setting.key)) continue;
          seen.add(entry.setting.key);
          out.push({ ...entry, group: group.title, scope });
        }
      }
    }
  }
  return out;
}

/**
 * ═══ « DANS LE MENU », « EN TUILE », « FERMÉE », « OUVERTE SANS COMPTE » ═══
 *
 * ⚠️ AUCUNE FAMILLE NE LES DÉCLARE, et c'est voulu pour le panneau : le gestionnaire
 * « Pages du site » les pilote d'un seul geste, les redéclarer y mettrait quarante
 * interrupteurs à côté de lui (voir `PAGE_MIGRATIONS` dans le catalogue). Mais le
 * récapitulatif ne parcourait que le catalogue : l'organisatrice ouvrait l'accueil aux
 * visiteurs, mettait l'inscription en tuile, et n'en retrouvait rien dans « Tout ce qui
 * est réglé » (bug #292). On les engendre donc ici, pour lui seul.
 *
 * ⚠️ LES MÊMES INTERRUPTEURS QUE LE GESTIONNAIRE, et LES MÊMES DÉFAUTS QUE LE SITE
 * (`usePages`) : le menu d'une page fixe est allumé tant qu'on ne l'éteint pas, tout le
 * reste est éteint tant qu'on ne l'allume pas. Sans le défaut, une inscription éteinte
 * pour une seule typologie se lisait « vide » face à « Non », sans dire que les autres
 * la voient.
 */
function pageSwitches(
  pages: ReadonlyArray<SitePage>,
): Array<{ setting: Setting; absent: boolean }> {
  return pages.flatMap((page) => [
    {
      setting: {
        key: navKey(page),
        label: `${page.name} · dans le menu`,
        type: "boolean" as const,
      },
      absent: !page.nameKey && isFixed(page.key),
    },
    // L'accueil n'a pas de tuile : elle mènerait à l'écran où l'on se trouve.
    ...(FIXED_PAGES.find((p) => p.key === page.key)?.tile === false
      ? []
      : [
          {
            setting: {
              key: tileKey(page),
              label: `${page.name} · en tuile`,
              type: "boolean" as const,
            },
            absent: false,
          },
        ]),
    // L'accueil et l'inscription ne se ferment pas : voir `closedKey`.
    ...(isFixed(page.key)
      ? []
      : [
          {
            setting: {
              key: closedKey(page),
              label: `${page.name} · fermée`,
              type: "boolean" as const,
            },
            absent: false,
          },
        ]),
    // Seules les pages qui savent se rendre sans session ont l'interrupteur.
    ...(page.publishable
      ? [
          {
            setting: {
              key: publicKey(page),
              label: `${page.name} · ouverte sans compte`,
              type: "boolean" as const,
            },
            absent: false,
          },
        ]
      : []),
  ]);
}

/**
 * Two values are "the same" when an organiser would call them the same. An
 * absent value and an empty string both read as "nothing set", and flagging that
 * pair as a divergence would fill the table with rows nobody needs to look at.
 */
function same(a: unknown, b: unknown): boolean {
  const blank = (v: unknown) => v === undefined || v === null || v === "";
  if (blank(a) && blank(b)) return true;
  return a === b;
}

export function overview(
  rows: Rows,
  scope: Scope,
  typos: ReadonlyArray<{ id: number; name: string }>,
  /**
   * ⚠️ LE PARCOURS RANGÉ, sinon les réglages d'une 2e instance d'étape (le titre, le
   * formulaire, l'introduction d'une seconde « informations ») sont INVISIBLES au
   * récapitulatif — précisément l'outil censé retrouver une surcharge oubliée dans un coin.
   */
  steps: ReadonlyArray<Entry> = [],
  /**
   * ⚠️ ET LES EXEMPLAIRES AJOUTÉS — sections comme pages formulaire —, pour la même
   * raison : leurs clés sont suffixées, donc invisibles d'un parcours du catalogue qui
   * ne sait pas qu'ils existent, et une surcharge oubliée sur la page 2 est exactement
   * ce que ce tableau existe pour retrouver.
   */
  pages: ReadonlyArray<string> = [],
): OverviewRow[] {
  const at = (typoId: number | null) => ({ ...scope, typoId });
  const typoIds = typos.map((t) => t.id);

  const next = [
    ...new Set([
      ...steps
        .map((e) => e.instance)
        .filter((i): i is string => !!i && i !== FIRST_INSTANCE),
      ...pages.filter((i) => i !== FIRST_INSTANCE),
    ]),
  ];
  // Le récapitulatif ne liste pas les réglages engendrés depuis les FORMULAIRES : ils
  // nomment un formulaire, ils ne se surchargent pas par typologie. Le parcours, lui, fait
  // apparaître les clés suffixées des instances ajoutées.
  const contextes: ReadonlyArray<CatalogContext> = [
    { typos, forms: [], ...(steps.length ? { steps } : {}) },
    ...next.map((instance) => ({
      typos,
      forms: [],
      instance,
      ...(steps.length ? { steps } : {}),
    })),
  ];

  // Les pages du gestionnaire, exemplaires ajoutés et étapes servies compris : la même
  // liste que lit le site (`usePages`), lue dans la valeur commune.
  const read = (key: string) =>
    resolveValue(layersFor(rows, at(null)), at(null), key);
  const sitePages = [...withAdded(MANAGED_PAGES, read), ...stepPages(steps)];

  return catalogued(contextes, sitePages).map(
    ({ setting, group, scope: from, absent }) => {
      // Ce que le site lit, défaut compris. `isSet`, lui, reste lu sur ce qui est ÉCRIT :
      // un défaut n'est pas un réglage.
      const effective = (v: unknown) => (v === undefined ? absent : v);
      const raw = resolveValue(
        layersFor(rows, at(null)),
        at(null),
        setting.key,
      );
      const common = effective(raw);

      const cells = typoIds.map((typoId) => {
        const s = at(typoId);
        const layers = layersFor(rows, s);
        const typos = layers.language.typosV2?.[String(typoId)] ?? {};
        const fallback = layers.defaultLanguage.typosV2?.[String(typoId)] ?? {};
        return {
          typoId,
          value: resolveValue(layers, s, setting.key),
          // "Written at this typology" — in either language row, since a typology
          // override made in the reference language governs the others too.
          own:
            Object.prototype.hasOwnProperty.call(typos, setting.key) ||
            Object.prototype.hasOwnProperty.call(fallback, setting.key),
        };
      });

      return {
        key: setting.key,
        label: setting.label,
        group,
        ...(setting.section ? { section: setting.section } : {}),
        scope: from,
        type: setting.type,
        ...(setting.options ? { options: setting.options } : {}),
        common,
        cells: cells.map((c) => ({ ...c, value: effective(c.value) })),
        differs: cells.some((c) => !same(effective(c.value), common)),
        isSet: raw !== undefined || cells.some((c) => c.value !== undefined),
      };
    },
  );
}
